// web-vite/src/App.tsx
//
// Top-level layout: sidebar + content area. Each list screen gets its own
// Header (with date/time); each detail/add/edit subroute mounts its own
// Header with a back button (provided by the per-screen file).
//
// CRUD pattern (per resource — personnel, equipment, chambers, locations,
// access-points):
//   /<resource>             → list
//   /<resource>/add         → form (no id)
//   /<resource>/:id         → detail
//   /<resource>/:id/edit    → form (with id)

import React from 'react'
import {BrowserRouter, Navigate, Route, Routes} from 'react-router-dom'
import {Sidebar} from './components/Sidebar'
import {Header} from './components/Header'
import {ToastHost} from './components/common/Toast'
import {PersonnelRoster} from './screens/PersonnelRoster'
import {TagBoardScreen} from './screens/TagBoardScreen'
import {EquipmentList} from './screens/EquipmentList'
import {RescueChambersScreen} from './screens/RescueChambersScreen'
import {LocationsScreen} from './screens/LocationsScreen'
import {AccessPointsScreen} from './screens/AccessPointsScreen'
import {ActivityFeed} from './screens/ActivityFeed'
import {PersonnelDetailScreen} from './screens/personnel/PersonnelDetailScreen'
import {PersonnelFormScreen} from './screens/personnel/PersonnelFormScreen'
import {EquipmentDetailScreen} from './screens/equipment/EquipmentDetailScreen'
import {EquipmentFormScreen} from './screens/equipment/EquipmentFormScreen'
import {ChamberDetailScreen} from './screens/chamber/ChamberDetailScreen'
import {ChamberFormScreen} from './screens/chamber/ChamberFormScreen'
import {LocationDetailScreen} from './screens/location/LocationDetailScreen'
import {LocationFormScreen} from './screens/location/LocationFormScreen'
import {APDetailScreen} from './screens/location/APDetailScreen'
import {APFormScreen} from './screens/location/APFormScreen'
import {DeveloperAPIsScreen} from './screens/devapi/DeveloperAPIsScreen'
import {AuthProvider, useAuth} from './app/useAuth'
import {LoginScreen} from './screens/auth/LoginScreen'

// List screens reuse the same shell: <main><Header /><Screen /></main>.
// We define a tiny wrapper to avoid repeating the markup.
const ListShell: React.FC<{children: React.ReactNode}> = ({children}) => (
  <main className="flex flex-1 flex-col overflow-hidden">
    <Header />
    {children}
  </main>
)

// AuthGate decides whether to render the LoginScreen or the main app. While
// the initial getSession() is in flight we show a minimal Loading state to
// avoid a Login → app flicker on every reload.
const AuthGate: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {session, loading} = useAuth()
  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-background text-textMuted">
        Loading…
      </div>
    )
  }
  if (!session) {
    return <LoginScreen />
  }
  return <>{children}</>
}

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <AuthGate>
        <AppRoutes />
      </AuthGate>
    </AuthProvider>
  )
}

const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter>
      <div className="flex h-full">
        <Sidebar />
        <Routes>
          {/* Tag Board (no CRUD) */}
          <Route path="/" element={<ListShell><TagBoardScreen /></ListShell>} />

          {/* Personnel */}
          <Route path="/personnel"          element={<ListShell><PersonnelRoster /></ListShell>} />
          <Route path="/personnel/add"      element={<PersonnelFormScreen />} />
          <Route path="/personnel/:id"      element={<PersonnelDetailScreen />} />
          <Route path="/personnel/:id/edit" element={<PersonnelFormScreen />} />

          {/* Equipment */}
          <Route path="/equipment"          element={<ListShell><EquipmentList /></ListShell>} />
          <Route path="/equipment/add"      element={<EquipmentFormScreen />} />
          <Route path="/equipment/:id"      element={<EquipmentDetailScreen />} />
          <Route path="/equipment/:id/edit" element={<EquipmentFormScreen />} />

          {/* Rescue Chambers */}
          <Route path="/chambers"           element={<ListShell><RescueChambersScreen /></ListShell>} />
          <Route path="/chambers/add"       element={<ChamberFormScreen />} />
          <Route path="/chambers/:id"       element={<ChamberDetailScreen />} />
          <Route path="/chambers/:id/edit"  element={<ChamberFormScreen />} />

          {/* Locations */}
          <Route path="/locations"          element={<ListShell><LocationsScreen /></ListShell>} />
          <Route path="/locations/add"      element={<LocationFormScreen />} />
          <Route path="/locations/:id"      element={<LocationDetailScreen />} />
          <Route path="/locations/:id/edit" element={<LocationFormScreen />} />

          {/* Access Points */}
          <Route path="/access-points"          element={<ListShell><AccessPointsScreen /></ListShell>} />
          <Route path="/access-points/add"      element={<APFormScreen />} />
          <Route path="/access-points/:id"      element={<APDetailScreen />} />
          <Route path="/access-points/:id/edit" element={<APFormScreen />} />

          {/* Activity Feed — live merged tag_events + location_events stream */}
          <Route path="/activity" element={<ListShell><ActivityFeed /></ListShell>} />

          {/* Developer APIs (internal preview) */}
          <Route path="/dev-api" element={<ListShell><DeveloperAPIsScreen /></ListShell>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ToastHost />
      </div>
    </BrowserRouter>
  )
}

